import { IconDownload } from "@/assets/images/Icons";
import { formatarValorEmRealBR } from "@/components/utils/Utils";
import moment from "moment";
import { useTranslation } from "react-i18next";
import {
  DeskRateioResumo,
  isDeskRateada,
} from "../../components/DeskRateioResumo";
import { JORNADA_IDS } from "@/constants/jornadaIds";
import { calcularTotalPropostaCriacao } from "../../utils/calcularTotalPropostaCriacao";

function normalizeLinks(value: any) {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string" || !value.trim()) return [];

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

interface FichaTecnicaProps {
  data: any;
  agency: any;
  client: any;
  status: any;
  produtos: any;
  type: any;
  totalMidia: any;
  isOpen: boolean;
  onToggle: () => void;
}

export function FichaTecnica({
  data,
  agency,
  client,
  status,
  produtos,
  type,
  totalMidia,
  isOpen,
  onToggle,
}: FichaTecnicaProps) {
  const { t } = useTranslation();
  const totalFichaTecnica =
    Number(totalMidia) > 0
      ? Number(totalMidia)
      : data?.jornada_id === JORNADA_IDS.CRIACAO_STAKEHOLDER
        ? calcularTotalPropostaCriacao(data?.proposta)
        : 0;
  const links = normalizeLinks(data?.links);

  return (
    <div className="sm:max-w-72 w-[95vw] bg-white p-4 sm:p-6 rounded-3xl shadow-xl relative transition-all duration-300 ease-in-out">
      <div className="rounded-full bg-azul-400 text-white text-xs px-2 py-1 inline-flex absolute -top-2 right-2 z-10">
        {data?.id}
      </div>

      <div className="flex items-center justify-between mb-2">
        <h1 className="font-medium text-cinza-800 text-2xl">{t("resumoDesk.fichaTecnica.title")}</h1>
        <button
          onClick={onToggle}
          className="text-azul-400 hover:text-azul-500 transition-colors duration-200 text-xs font-medium flex items-center gap-1"
        >
          {isOpen ? t("common:see_less") : t("common:see_more")}
          <svg
            className={`w-4 h-4 transition-transform duration-300 ${
              isOpen ? "rotate-180" : ""
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
      </div>

      <div
        className={`flex flex-col gap-y-1 p-1 text-sm text-cinza-800 font-medium overflow-hidden transition-all duration-500 ease-in-out ${
          isOpen ? "max-h-[2000px] opacity-100" : "max-h-16 opacity-60"
        }`}
      >
        {agency && (
          <div>
            <h3>{t("resumoDesk.fichaTecnica.agency")}:</h3>
            <p className="text-cinza-400 w-fit text-xs">{agency}</p>
          </div>
        )}
        <div>
          <h3>{t("resumoDesk.fichaTecnica.name")}:</h3>
          <p className="text-cinza-400 w-fit text-xs">
            {data?.name ?? data?.title ?? data?.titulo ?? data?.conceito}
          </p>
        </div>
        <div>
          <h3>{t("resumoDesk.fichaTecnica.date")}:</h3>
          <p className="text-cinza-400 w-fit text-xs">
            {type === "desk" && (
              <>
                {moment.utc(data.date_init).format("DD/MM/YYYY")} à{" "}
                {moment.utc(data.date_end).format("DD/MM/YYYY")}
              </>
            )}
            {type === "reunion" && (
              <>{moment.utc(data.date).format("DD/MM/YYYY - HH:mm")}</>
            )}
            {type === "prospecting" && (
              <>{moment.utc(data.date).format("DD/MM/YYYY")}</>
            )}
            {type === "campanha" && (
              <>
                {data?.mes ? String(data.mes).padStart(2, "0") : "--"}/
                {data?.ano ?? "----"}
              </>
            )}
            {type === "cronograma" && (
              <>
                {moment.utc(data.data).format("DD/MM/YYYY")}
                {data?.horario ? ` - ${data.horario}` : ""}
              </>
            )}
          </p>
        </div>
        {client && (
          <div>
            <h3>{t("resumoDesk.fichaTecnica.client")}:</h3>
            <p className="text-cinza-400 w-fit text-xs">{client}</p>
          </div>
        )}
        {data.produto && (
          <div>
            <h3>{t("resumoDesk.fichaTecnica.product")}:</h3>
            <p className="text-cinza-400 w-fit text-xs">
              {produtos.find((option: any) => option.value == data.produto)
                ?.label || t("resumoDesk.fichaTecnica.product_not_found")}
            </p>
          </div>
        )}
        {data?.numero_pi && (
          <div>
            <h3>{t("resumoDesk.fichaTecnica.pi")}:</h3>
            <p className="text-cinza-400 w-fit text-xs">{data?.numero_pi}</p>
          </div>
        )}
        {status && (
          <div>
            <h3 className="">{t("resumoDesk.fichaTecnica.status")}:</h3>
            <p className="text-cinza-400 w-fit text-xs">{status}</p>
          </div>
        )}
        {data?.priority && (
          <div>
            <h3>{t("resumoDesk.fichaTecnica.priority")}:</h3>
            <p className="text-cinza-400 w-fit text-xs">
              {data?.priority?.name}
            </p>
          </div>
        )}
        {data?.valor_proposta > 0 && !isDeskRateada(data) && (
          <div>
            <h3>{t("resumoDesk.fichaTecnica.proposal_value")}:</h3>
            <p className="text-cinza-400 w-fit text-xs">
              {formatarValorEmRealBR(data.valor_proposta)}
            </p>
          </div>
        )}
        {type === "campanha" && data?.valor && (
          <div>
            <h3>{t("resumoDesk.fichaTecnica.total")}:</h3>
            <p className="text-cinza-400 w-fit text-xs">
              {formatarValorEmRealBR(data.valor)}
            </p>
          </div>
        )}
        {isDeskRateada(data) && (
          <DeskRateioResumo desk={data} compact className="mt-2" />
        )}
        {totalFichaTecnica > 0 && (
          <div>
            <h3>{t("resumoDesk.fichaTecnica.total")}:</h3>
            <p className="text-cinza-400 w-fit text-xs">
              {formatarValorEmRealBR(totalFichaTecnica)}
            </p>
          </div>
        )}
        {links.length > 0 && (
          <div>
            <h3>{t("resumoDesk.fichaTecnica.links")}:</h3>
            <ul className="space-y-1">
              {links.map((item: any, index: any) => {
                const link = typeof item === "string" ? item : item.link;
                const displayName =
                  typeof item === "object" && item.nome ? item.nome : link;

                return (
                  <li key={index} className="">
                    <a
                      href={link}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={link}
                      className="flex items-center justify-between gap-x-2 px-2 py-1 bg-[#EBEBEB] rounded-md text-cinza-500 max-w-full text-xs truncate uppercase hover:text-azul-400 transition-all"
                    >
                      {displayName}
                      <span className="w-4">
                        <IconDownload />
                      </span>
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>

      {!isOpen && (
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent rounded-b-3xl pointer-events-none" />
      )}
    </div>
  );
}
